// src/components/UploadCaseModal.jsx
//
// Modal for creating a new case (baseline + first follow-up + interval + label).
// The disclosure checkbox is required by the backend.

import React, { useRef, useState } from 'react';
import { apiPostForm, ApiError } from '../api';
import { useAuth } from '../contexts/AuthContext';
import './UploadCaseModal.css';

const INTERVALS = [
  { value: 6,  label: '6 months' },
  { value: 12, label: '12 months' },
  { value: 24, label: '24 months' },
];

const MAX_MB = 50;

function isValidNiftiName(name) {
  if (!name) return false;
  const n = name.toLowerCase();
  return n.endsWith('.nii') || n.endsWith('.nii.gz');
}

export default function UploadCaseModal({ onClose, onCreated }) {
  const { user } = useAuth();

  const baselineRef = useRef(null);
  const followupRef = useRef(null);

  const [baselineFile, setBaselineFile] = useState(null);
  const [followupFile, setFollowupFile] = useState(null);
  const [interval, setInterval] = useState(12);
  const [subjectLabel, setSubjectLabel] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  function pickFile(setter, file) {
    if (!file) { setter(null); return; }
    if (!isValidNiftiName(file.name)) {
      setError(`"${file.name}" — must be a .nii or .nii.gz file.`);
      setter(null);
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`"${file.name}" is over ${MAX_MB} MB. Please compress or trim.`);
      setter(null);
      return;
    }
    setError(null);
    setter(file);
  }

  const canSubmit =
    baselineFile && followupFile && interval && confirmed && !uploading;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setUploading(true);
    setProgress(0);

    const fd = new FormData();
    fd.append('baseline', baselineFile);
    fd.append('followup', followupFile);
    fd.append('interval_months', String(interval));
    fd.append('confirmed_deidentified', 'true');
    if (subjectLabel.trim()) fd.append('subject_label', subjectLabel.trim());

    try {
      const totalBytes = baselineFile.size + followupFile.size;
      const result = await apiPostForm('/api/cases', fd, user, (sent) => {
        setProgress(totalBytes > 0 ? sent / totalBytes : 0);
      });
      onCreated?.(result.case);
      onClose?.();
    } catch (err) {
      const msg = err instanceof ApiError ? (err.detail || err.message) : 'Upload failed.';
      setError(msg);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true"
           onClick={(e) => e.stopPropagation()}>
        <header className="modal__head">
          <h2 className="modal__title">Upload a new case</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="modal__body">
          <p className="modal__lead">
            Provide the baseline MRI and one follow-up scan. You can add more
            follow-ups (at different intervals) after this case is created.
          </p>

          <form onSubmit={handleSubmit} className="form">
            {/* Subject label (optional) */}
            <div className="form__field">
              <label htmlFor="subject_label">Case label (optional)</label>
              <input
                id="subject_label"
                type="text"
                value={subjectLabel}
                onChange={(e) => setSubjectLabel(e.target.value.slice(0, 80))}
                placeholder="e.g. Patient A, MCI follow-up 2026-01"
                disabled={uploading}
              />
              <span className="form__hint">
                Visible only to you. Use a code, not a name.
              </span>
            </div>

            {/* Baseline file */}
            <div className="form__field">
              <label htmlFor="baseline_file">Baseline scan (.nii / .nii.gz)</label>
              <input
                id="baseline_file"
                ref={baselineRef}
                type="file"
                accept=".nii,.nii.gz,application/gzip"
                onChange={(e) => pickFile(setBaselineFile, e.target.files?.[0])}
                disabled={uploading}
                required
              />
              {baselineFile && (
                <span className="form__filename">
                  {baselineFile.name} ({(baselineFile.size / (1024*1024)).toFixed(1)} MB)
                </span>
              )}
            </div>

            {/* Followup file */}
            <div className="form__field">
              <label htmlFor="followup_file">Follow-up scan (.nii / .nii.gz)</label>
              <input
                id="followup_file"
                ref={followupRef}
                type="file"
                accept=".nii,.nii.gz,application/gzip"
                onChange={(e) => pickFile(setFollowupFile, e.target.files?.[0])}
                disabled={uploading}
                required
              />
              {followupFile && (
                <span className="form__filename">
                  {followupFile.name} ({(followupFile.size / (1024*1024)).toFixed(1)} MB)
                </span>
              )}
            </div>

            {/* Interval */}
            <div className="form__field">
              <label>Follow-up interval (months after baseline)</label>
              <div className="form__radios">
                {INTERVALS.map((i) => (
                  <label key={i.value} className="form__radio">
                    <input
                      type="radio"
                      name="interval"
                      value={i.value}
                      checked={interval === i.value}
                      onChange={() => setInterval(i.value)}
                      disabled={uploading}
                    />
                    {i.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Disclosure */}
            <label className="form__check">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={uploading}
              />
              <span>
                I confirm these scans are <strong>de-identified</strong> and I have
                authority to upload them. <strong>Research use only;</strong> the
                output is not for clinical decisions.
              </span>
            </label>

            {/* Error */}
            {error && (
              <div className="form__error" role="alert">{error}</div>
            )}

            {/* Progress */}
            {uploading && (
              <div className="form__progress" aria-live="polite">
                <div className="form__progress-track">
                  <div
                    className="form__progress-fill"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>
                <span className="form__progress-label">
                  Uploading… {Math.round(progress * 100)}%
                </span>
              </div>
            )}

            <div className="form__actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClose}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!canSubmit}
              >
                {uploading ? 'Uploading…' : 'Upload case'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
